// ===================== Persistent meta-progression (localStorage) =====================
const KEY = 'neonfang.save.v1';
const SAVE_VERSION = 5;

const DEFAULT_SAVE = {
  version: SAVE_VERSION,
  coins: 0,               // meta currency (banked gold)
  best: {},               // character id -> best survival seconds
  bestKills: 0,
  bestTime: 0,            // best survival across all characters
  runs: 0,
  totalKills: 0,
  unlocked: ['wanderer'], // unlocked character ids
  stages: ['meadow'],     // unlocked stage ids (biomes reached on the world map)
  // Hex world map: conquered node ids, the ones the horde has taken back, the
  // incursion clock (epoch ms) driving the reclamation, and threat mastery —
  // `threat` is the highest tier beaten per hex, `threatMax` the highest beaten
  // anywhere (which is what gates the tiers you may select).
  map: {
    conquered: [], contested: [], nextSiegeAt: 0, sieges: 0, seeded: false,
    threat: {}, threatMax: 0,
    overrun: {},            // nodeId -> best OVERRUN survival (seconds past the finale)
    // Today's hex anomalies: the UTC day they were rolled for and the picks
    // ({nodeId, mutId}). Stored picks are canonical for the day — mid-day
    // conquests or reclaims never reshuffle them.
    anomalies: { day: 0, picks: [] },
  },
  // Per-save salt for the daily anomaly roll. 0 = "not generated yet": it is
  // lazily created (save-layer Math.random, not sim code) on first use, so a
  // reset naturally re-rolls it too.
  anomalySeed: 0,
  // THE WEEKLY GAUNTLET — one attempt per ISO week on a globally shared seed.
  // `week` is the attempt's week key, `attempted`/`cleared` cover that week,
  // `clears`/`bestSeconds`/`bestScore` are lifetime.
  gauntlet: { week: 0, attempted: false, cleared: false, clears: 0, bestSeconds: 0, bestScore: 0 },
  achievements: [],       // unlocked achievement ids
  seen: [],               // discovered element/weapon ids (bestiary flavor)
  upgrades: {},           // permanent skill-tree node levels {id: level}
  prestige: 0,            // Ascension count (tree resets exchanged for power)
  tutorialDone: false,    // first-run hints completed/skipped
  iap: { owned: [] },     // non-consumable purchases (native builds)
  settings: null,         // populated lazily by engine/settings.js
  muted: false,           // legacy; migrated into settings
};

// Set when the stored save could not be read or written. Surfaced so the game
// can tell the player rather than silently losing their progress.
// `lastWriteAt` (epoch ms) is the last successful flush — it lets the UI say
// how much progress is actually at risk when writes start failing.
export const SaveStatus = { load: null, write: null, lastWriteAt: null };

// ---- Export / import (device-loss insurance) ----
// Format: `NEONFANG1.<fnv1a-hex>.<base64(JSON)>`. The version-bearing prefix
// distinguishes future formats, the checksum catches truncated or mangled
// pastes, and base64 keeps the code copy/paste-safe. The payload is the raw
// stored JSON — import writes it back verbatim and the app reloads, so an
// imported save goes through the exact same load()/migrate() chain a stored
// one does (including the future-version and corrupt quarantines).
const EXPORT_PREFIX = 'NEONFANG1.';

// FNV-1a over the JSON string, hex-encoded. Not cryptographic — it only needs
// to catch transit corruption, not adversaries (it's the player's own save).
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// btoa/atob only take latin-1, so round-trip through UTF-8 bytes.
function b64encode(str) {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function b64decode(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

// Validate an exported code without touching storage. Returns
// { ok:true, json, future } or { ok:false, error } with a player-readable
// reason. `future` mirrors migrate()'s version check: a code from a newer
// build is importable — load() quarantines the original and plays with what
// this build understands, same as a newer stored save.
export function decodeExport(text) {
  // Whitespace (line wraps from email/notes apps) is never meaningful.
  const code = String(text || '').replace(/\s+/g, '');
  if (!code) return { ok: false, error: 'the code is empty' };
  if (!code.startsWith(EXPORT_PREFIX)) return { ok: false, error: 'this is not a NEONFANG save code' };
  const rest = code.slice(EXPORT_PREFIX.length);
  const dot = rest.indexOf('.');
  if (dot < 0) return { ok: false, error: 'the code is incomplete' };
  const sum = rest.slice(0, dot);
  let json;
  try { json = b64decode(rest.slice(dot + 1)); } catch { return { ok: false, error: 'the code is garbled' }; }
  if (fnv1a(json) !== sum) return { ok: false, error: 'the code failed its integrity check — part of it is missing or altered' };
  let data;
  try { data = JSON.parse(json); } catch { return { ok: false, error: 'the code does not contain save data' }; }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { ok: false, error: 'the code does not contain save data' };
  }
  return { ok: true, json, future: (data.version || 1) > SAVE_VERSION };
}

// DEFAULT_SAVE is pure JSON, so a round-trip is a safe deep clone. This must
// never be a spread: the nested arrays and objects would alias the module-level
// default, letting gameplay mutate it and survive a reset.
function freshSave() { return JSON.parse(JSON.stringify(DEFAULT_SAVE)); }

// Additive, forward-compatible migration. Never throws.
function migrate(data) {
  if (!data || typeof data !== 'object') return freshSave();
  const v = data.version || 1;
  // A save from a NEWER build (a downgrade, or a synced backup from another
  // device). Running the migration chain over it would be meaningless and the
  // merge below would quietly drop fields we don't know about — so leave it be.
  if (v > SAVE_VERSION) { SaveStatus.load = 'future'; return data; }
  if (v < 2) {
    // v1 -> v2: fold legacy top-level `muted` into settings.
    data.settings = data.settings || {};
    if (typeof data.muted === 'boolean' && data.settings.muted === undefined) {
      data.settings.muted = data.muted;
    }
    data.version = 2;
  }
  if (v < 3) {
    // v2 -> v3: the flat stage list became the hex world map. The map itself
    // seeds conquered hexes from `stages` on first load (see content/worldmap.js)
    // — here we only make sure the container exists.
    data.map = data.map && typeof data.map === 'object' ? data.map : { conquered: [], seeded: false };
    data.version = 3;
  }
  if (v < 4) {
    // v3 -> v4: daily hex anomalies + the Weekly Gauntlet. The anomaly seed
    // stays 0 here — it is generated lazily on first use, not during migration.
    if (!data.anomalySeed) data.anomalySeed = 0;
    data.map = data.map && typeof data.map === 'object' ? data.map : { conquered: [], seeded: false };
    if (!data.map.anomalies || typeof data.map.anomalies !== 'object') {
      data.map.anomalies = { day: 0, picks: [] };
    }
    if (!data.gauntlet || typeof data.gauntlet !== 'object') {
      data.gauntlet = { week: 0, attempted: false, cleared: false, clears: 0, bestSeconds: 0 };
    }
    data.version = 4;
  }
  if (v < 5) {
    // v4 -> v5: Gauntlet scoring (a lifetime best-score joins the block) and
    // per-hex OVERRUN depth records. Both default empty/zero.
    if (data.gauntlet && typeof data.gauntlet === 'object' && typeof data.gauntlet.bestScore !== 'number') {
      data.gauntlet.bestScore = 0;
    }
    if (data.map && typeof data.map === 'object' && (!data.map.overrun || typeof data.map.overrun !== 'object')) {
      data.map.overrun = {};
    }
    data.version = 5;
  }
  return data;
}

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return freshSave();
    const data = migrate(JSON.parse(raw));
    // A save from a NEWER build: the normalizing merge below rebuilds map/best/
    // iap/settings from field lists this build knows about, and the first
    // flush() would persist that stripped copy. Quarantine the raw string
    // FIRST (mirroring the corrupt path) so the original stays recoverable
    // even though this build plays with what it understands.
    if (SaveStatus.load === 'future') {
      try { localStorage.setItem(KEY + '.future', raw); } catch { /* best effort */ }
    }
    return { ...freshSave(), ...data,
      best: { ...(data.best || {}) },
      upgrades: { ...(data.upgrades || {}) },
      settings: data.settings ? { ...data.settings } : null,
      unlocked: Array.from(new Set([...(DEFAULT_SAVE.unlocked), ...(data.unlocked || [])])),
      stages: Array.from(new Set([...(DEFAULT_SAVE.stages), ...(data.stages || [])])),
      map: {
        conquered: Array.from(new Set([...((data.map && data.map.conquered) || [])])),
        contested: Array.from(new Set([...((data.map && data.map.contested) || [])])),
        nextSiegeAt: (data.map && data.map.nextSiegeAt) || 0,
        sieges: (data.map && data.map.sieges) || 0,
        seeded: !!(data.map && data.map.seeded),
        threat: (data.map && data.map.threat) || {},
        threatMax: (data.map && data.map.threatMax) || 0,
        overrun: { ...((data.map && data.map.overrun) || {}) },
        // Rebuilt field-by-field like the rest of `map` — forgetting this here
        // would silently drop the day's picks on every load.
        anomalies: {
          day: (data.map && data.map.anomalies && data.map.anomalies.day) || 0,
          picks: Array.isArray(data.map && data.map.anomalies && data.map.anomalies.picks)
            ? [...data.map.anomalies.picks] : [],
        },
      },
      anomalySeed: data.anomalySeed || 0,
      gauntlet: {
        week: (data.gauntlet && data.gauntlet.week) || 0,
        attempted: !!(data.gauntlet && data.gauntlet.attempted),
        cleared: !!(data.gauntlet && data.gauntlet.cleared),
        clears: (data.gauntlet && data.gauntlet.clears) || 0,
        bestSeconds: (data.gauntlet && data.gauntlet.bestSeconds) || 0,
        bestScore: (data.gauntlet && data.gauntlet.bestScore) || 0,
      },
      achievements: Array.from(new Set([...(data.achievements || [])])),
      iap: { owned: [...((data.iap && data.iap.owned) || [])] },
    };
  } catch {
    // Unreadable save. Do NOT just start fresh and let the next flush()
    // overwrite it — that turns a recoverable parse error into a permanent,
    // silent wipe of everything the player has. Quarantine the raw string so
    // it can be recovered, and flag it so the game can say what happened.
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) localStorage.setItem(KEY + '.corrupt', raw);
    } catch { /* nothing more we can do */ }
    SaveStatus.load = 'corrupt';
    return freshSave();
  }
}

export const Save = {
  data: load(),
  status() { return { ...SaveStatus }; },
  // Optional app-layer listener, fired when writes START failing (once per
  // healthy→failing transition, so it can never spam). The save layer must not
  // import UI — main.js assigns this the same way boot() polls status().
  onWriteFailure: null,
  // True on success. A failed write is remembered and warned about once —
  // silently swallowing a quota error means the player keeps playing and loses
  // the session on reload with no indication anything went wrong.
  flush() {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
      SaveStatus.write = null;
      SaveStatus.lastWriteAt = Date.now();
      return true;
    } catch {
      if (SaveStatus.write !== 'quota') {
        SaveStatus.write = 'quota';
        // Only worth saying out loud where storage exists but refused us — in
        // Node (build scripts, validators) there is simply no localStorage.
        if (typeof localStorage !== 'undefined') {
          console.warn('NEONFANG: progress could not be saved (storage full or blocked).');
        }
        // The UI hook must never be able to break saving itself.
        if (typeof this.onWriteFailure === 'function') {
          try { this.onWriteFailure(); } catch { /* toast trouble is not our problem */ }
        }
      }
      return false;
    }
  },
  // The current save as a copy/paste-safe code (see decodeExport for format).
  exportSave() {
    const json = JSON.stringify(this.data);
    return EXPORT_PREFIX + fnv1a(json) + '.' + b64encode(json);
  },
  // Validate a code and stage it as the stored save. NEVER destructive: the
  // current save is quarantined to `.backup` first (mirroring the `.corrupt` /
  // `.future` pattern), and the imported payload is written raw — the caller
  // reloads, so the real load()/migrate() chain is what boots from it, exactly
  // as if it had always been the stored save. Returns decodeExport's shape.
  importSave(text) {
    const d = decodeExport(text);
    if (!d.ok) return d;
    try {
      const cur = localStorage.getItem(KEY);
      if (cur) localStorage.setItem(KEY + '.backup', cur);
      localStorage.setItem(KEY, d.json);
    } catch {
      return { ok: false, error: 'device storage refused the write (full or blocked)' };
    }
    return { ok: true, future: d.future };
  },
  addCoins(n) { this.data.coins += n; this.flush(); },
  spendCoins(n) { if (this.data.coins >= n) { this.data.coins -= n; this.flush(); return true; } return false; },
  unlock(id) {
    if (!this.data.unlocked.includes(id)) { this.data.unlocked.push(id); this.flush(); return true; }
    return false;
  },
  isUnlocked(id) { return this.data.unlocked.includes(id); },
  unlockStage(id) {
    if (!this.data.stages) this.data.stages = ['meadow'];
    if (!this.data.stages.includes(id)) { this.data.stages.push(id); this.flush(); return true; }
    return false;
  },
  isStageUnlocked(id) { return (this.data.stages || ['meadow']).includes(id); },

  // ---- hex world map ----
  _map() {
    const m = this.data.map && typeof this.data.map === 'object' ? this.data.map : (this.data.map = {});
    if (!Array.isArray(m.conquered)) m.conquered = [];
    if (!Array.isArray(m.contested)) m.contested = [];
    if (typeof m.nextSiegeAt !== 'number') m.nextSiegeAt = 0;
    if (typeof m.sieges !== 'number') m.sieges = 0;
    if (!m.threat || typeof m.threat !== 'object') m.threat = {};
    if (typeof m.threatMax !== 'number') m.threatMax = 0;
    if (!m.anomalies || typeof m.anomalies !== 'object') m.anomalies = { day: 0, picks: [] };
    if (!Array.isArray(m.anomalies.picks)) m.anomalies.picks = [];
    if (!m.overrun || typeof m.overrun !== 'object') m.overrun = {};
    return m;
  },

  // ---- daily hex anomalies ----
  // The per-save salt for the daily roll. Generated lazily when falsy —
  // save-layer entropy, not sim code, so Math.random is fine here. `|| 1`
  // keeps a legitimate roll of 0 from re-rolling forever.
  anomalySeed() {
    if (!this.data.anomalySeed) {
      this.data.anomalySeed = ((Math.random() * 0x100000000) >>> 0) || 1;
      this.flush();
    }
    return this.data.anomalySeed;
  },
  anomalies() { return this._map().anomalies; },
  setAnomalies(day, picks) { this._map().anomalies = { day, picks }; this.flush(); },

  // ---- the Weekly Gauntlet ----
  _gauntlet() {
    const g = this.data.gauntlet && typeof this.data.gauntlet === 'object'
      ? this.data.gauntlet : (this.data.gauntlet = {});
    if (typeof g.week !== 'number') g.week = 0;
    if (typeof g.attempted !== 'boolean') g.attempted = false;
    if (typeof g.cleared !== 'boolean') g.cleared = false;
    if (typeof g.clears !== 'number') g.clears = 0;
    if (typeof g.bestSeconds !== 'number') g.bestSeconds = 0;
    if (typeof g.bestScore !== 'number') g.bestScore = 0;
    return g;
  },
  gauntletState() { return { ...this._gauntlet() }; },
  gauntletAttempted(week) { const g = this._gauntlet(); return g.week === week && g.attempted; },
  // Consume the week's single attempt. Called AT RUN START and flushed
  // immediately, so quitting out (or crashing) never refunds it.
  startGauntletAttempt(week) {
    const g = this._gauntlet();
    if (g.week === week && g.attempted) return false;
    g.week = week; g.attempted = true; g.cleared = false;
    this.flush();
    return true;
  },
  // Bank the run's outcome. A win marks the week cleared (once) and counts a
  // lifetime clear; any run can set a new personal-best survival time or score.
  recordGauntletResult(week, seconds, won, score = 0) {
    const g = this._gauntlet();
    if (seconds > g.bestSeconds) g.bestSeconds = seconds;
    const newBestScore = score > g.bestScore;
    if (newBestScore) g.bestScore = score;
    if (won && !(g.week === week && g.cleared)) { g.week = week; g.cleared = true; g.clears++; }
    this.flush();
    return { clears: g.clears, newBestScore };
  },

  // ---- OVERRUN depth records ----
  overrunBest(nodeId) { return this._map().overrun[nodeId] || 0; },
  // Bank a run's overrun survival. Returns true when it beats the hex's record.
  recordOverrun(nodeId, seconds) {
    if (!nodeId || !seconds) return false;
    const m = this._map();
    if (seconds <= (m.overrun[nodeId] || 0)) return false;
    m.overrun[nodeId] = seconds;
    this.flush();
    return true;
  },

  // ---- threat mastery ----
  threatOf(nodeId) { return this._map().threat[nodeId] || 0; },
  maxThreat() { return this._map().threatMax; },
  // Bank a cleared tier. Returns true when it beats this hex's own record.
  recordThreat(nodeId, tier) {
    if (!tier) return false;
    const m = this._map();
    if (tier > m.threatMax) m.threatMax = tier;
    if (tier <= (m.threat[nodeId] || 0)) { this.flush(); return false; }
    m.threat[nodeId] = tier;
    this.flush();
    return true;
  },
  // Contested hexes stay "conquered" for adjacency and gating — losing one can
  // never shut a route you already opened. It costs you the hex's benefits.
  isConquered(nodeId) { return this._map().conquered.includes(nodeId); },
  isContested(nodeId) { return this._map().contested.includes(nodeId); },
  contestedIds() { return [...this._map().contested]; },
  // Claim a hex; returns true only on the first clear (so callers pay the bounty once).
  conquer(nodeId) {
    const m = this._map();
    if (m.conquered.includes(nodeId)) return false;
    m.conquered.push(nodeId);
    this.flush();
    return true;
  },
  conqueredCount() { return this._map().conquered.length; },
  // The horde takes a hex back.
  contest(nodeId) {
    const m = this._map();
    if (!m.conquered.includes(nodeId) || m.contested.includes(nodeId)) return false;
    m.contested.push(nodeId);
    m.sieges++;
    this.flush();
    return true;
  },
  // …and you take it off them again.
  reclaim(nodeId) {
    const m = this._map();
    const i = m.contested.indexOf(nodeId);
    if (i < 0) return false;
    m.contested.splice(i, 1);
    this.flush();
    return true;
  },
  siegeClock() { return this._map().nextSiegeAt; },
  setSiegeClock(at) { this._map().nextSiegeAt = at; this.flush(); },
  siegeCount() { return this._map().sieges; },
  // One-time backfill for saves that predate the map (see content/worldmap.js).
  seedMap(nodeIds) {
    const m = this._map();
    if (m.seeded) return false;
    for (const id of nodeIds) if (!m.conquered.includes(id)) m.conquered.push(id);
    m.seeded = true;
    this.flush();
    return true;
  },
  isAchieved(id) { return (this.data.achievements || []).includes(id); },
  // Unlock an achievement; returns true only on first unlock (so callers toast once).
  achieve(id) {
    if (!this.data.achievements) this.data.achievements = [];
    if (this.data.achievements.includes(id)) return false;
    this.data.achievements.push(id);
    this.flush();
    return true;
  },
  recordRun({ character, seconds, kills, coins }) {
    this.data.runs++;
    this.data.totalKills += kills;
    if (kills > this.data.bestKills) this.data.bestKills = kills;
    if (seconds > (this.data.bestTime || 0)) this.data.bestTime = seconds;
    if (!this.data.best[character] || seconds > this.data.best[character]) this.data.best[character] = seconds;
    this.data.coins += coins;
    this.flush();
  },
  metaLevel(id) { return this.data.upgrades[id] || 0; },
  buyMeta(id) { this.data.upgrades[id] = (this.data.upgrades[id] || 0) + 1; this.flush(); },
  // ASCENSION: wipe the skill tree in exchange for a permanent prestige rank.
  ascend() {
    this.data.prestige = (this.data.prestige || 0) + 1;
    this.data.upgrades = {};
    this.flush();
    return this.data.prestige;
  },
  ownsIap(sku) { return !!(this.data.iap && this.data.iap.owned.includes(sku)); },
  grantIap(sku) {
    if (!this.data.iap) this.data.iap = { owned: [] };
    if (!this.data.iap.owned.includes(sku)) { this.data.iap.owned.push(sku); this.flush(); return true; }
    return false;
  },
  reset() {
    // Paid entitlements and options survive a save wipe; all else starts fresh.
    const settings = this.data.settings;
    const iap = this.data.iap || { owned: [] };
    // freshSave() covers every field by construction — listing them by hand is
    // how `seen` got missed, which handed back ultraweapon progress after a wipe.
    this.data = freshSave();
    this.data.map.seeded = true;          // don't re-seed from the legacy stage list
    this.data.settings = this.data.settings || settings;
    this.data.iap = iap;
    this.flush();
  },
};
